import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Field, Input } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { usePageTitle } from '../../lib/usePageTitle';
import { AuthShell } from './AuthShell';

export function ForgotPassword() {
  usePageTitle('Reset your password — WaveQA');

  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      await api.forgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthShell title="Check your email">
        <Alert tone="info">
          If that address has an account, a reset link is on its way. The link
          expires in 30 minutes and can only be used once.
        </Alert>
        <p className="text-sm text-hull-300">
          The wording is deliberately non-committal — confirming which addresses
          have accounts would make this page a way to enumerate customers.
        </p>
        <div className="mt-5">
          <Link to="/login" className="text-sm text-surge-400 hover:text-surge-300">
            ← Back to sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter the address you registered with and we will send you a link."
      footer={
        <Link to="/login" className="text-surge-400 hover:text-surge-300">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        {error && <Alert tone="error">{error}</Alert>}

        <Field label="Email" htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>

        <Button type="submit" disabled={busy} className="w-full">
          {busy ? 'Sending…' : 'Send reset link'}
        </Button>
      </form>
    </AuthShell>
  );
}
